import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth, ROLE_HOME } from "./context/AuthContext";
import { Loading } from "./components/Shared";
import Nav from "./components/marketing/Nav";
import Footer from "./components/marketing/Footer";

import Home from "./pages/marketing/Home";
import ServicesPage from "./pages/marketing/Services";
import ForFamilies from "./pages/marketing/ForFamilies";
import ForParents from "./pages/marketing/ForParents";
import CareManagerPage from "./pages/marketing/CareManagerPage";
import PlansPage from "./pages/marketing/PlansPage";
import Trust from "./pages/marketing/Trust";
import About from "./pages/marketing/About";
import Contact from "./pages/marketing/Contact";
import FAQ from "./pages/marketing/FAQ";
import Careers from "./pages/marketing/Careers";
import { Privacy, Terms, DataDeletion } from "./pages/marketing/Legal";
import Login from "./pages/Login";
import Onboarding from "./pages/Onboarding";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

import FamilyLayout from "./pages/family/FamilyLayout";
import Overview from "./pages/family/Overview";
import Timeline from "./pages/family/Timeline";
import Visits from "./pages/family/Visits";
import CalendarPage from "./pages/family/CalendarPage";
import Requests from "./pages/family/Requests";
import Members from "./pages/family/Members";
import CareFile from "./pages/family/CareFile";
import Documents from "./pages/family/Documents";
import Billing from "./pages/family/Billing";
import Communication from "./pages/family/Communication";

import SeniorMode from "./pages/senior/SeniorMode";
import CMLayout from "./pages/cm/CMLayout";
import CMToday from "./pages/cm/CMToday";
import CMVisit from "./pages/cm/CMVisit";

import AdminLayout from "./pages/admin/AdminLayout";
import Staff from "./pages/admin/Staff";
import AdminToday from "./pages/admin/AdminToday";
import Customers from "./pages/admin/Customers";
import CareManagers from "./pages/admin/CareManagers";
import ServicesAdmin from "./pages/admin/ServicesAdmin";
import Providers from "./pages/admin/Providers";
import Emergency from "./pages/admin/Emergency";
import Cases from "./pages/admin/Cases";
import Reports from "./pages/admin/Reports";
import PlansAdmin from "./pages/admin/PlansAdmin";
import Leads from "./pages/admin/Leads";
import AdminNotifications from "./pages/admin/Notifications";
import ServiceRequests from "./pages/admin/ServiceRequests";
import ServiceRequestDetail from "./pages/admin/ServiceRequestDetail";
import VisitDetail from "./pages/admin/VisitDetail";
import WhatsAppSettings from "./pages/admin/WhatsAppSettings";
import Profile from "./pages/Profile";

import { isNative } from "./native";
import { NativeFamilyShell, NativeCmShell, NativeLegalShell, AppHeader } from "./components/native/AppShell";
import FamilyHome from "./pages/native/FamilyHome";
import { FamilyCareHub, FamilyMore } from "./pages/native/FamilyHubs";
import { CmFamilies, CmVisits, CmAlerts } from "./pages/native/CmPages";

// True inside the Capacitor app — the SAME routes serve an app presentation instead
// of the website chrome. Business pages/logic are shared untouched.
const NATIVE = isNative();

function RequireAuth({ roles, children }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" state={{ from: loc.pathname }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={ROLE_HOME[user.role] || "/"} replace />;
  return children;
}

function RoleRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={ROLE_HOME[user.role] || "/"} replace />;
}

function Marketing({ children }) {
  return (<><Nav /><main>{children}</main><Footer /></>);
}

export default function App() {
  // Marketing pages: hidden in the app shell (legal pages stay reachable from More).
  const mkt = (el) => (NATIVE ? <Navigate to="/app" replace /> : <Marketing>{el}</Marketing>);
  const legal = (title, el) => (NATIVE ? <NativeLegalShell title={title}>{el}</NativeLegalShell> : <Marketing>{el}</Marketing>);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-center" richColors />
        <Routes>
          <Route path="/" element={NATIVE ? <Navigate to="/app" replace /> : <Marketing><Home /></Marketing>} />
          <Route path="/services" element={mkt(<ServicesPage />)} />
          <Route path="/for-families" element={mkt(<ForFamilies />)} />
          <Route path="/for-parents" element={mkt(<ForParents />)} />
          <Route path="/care-manager" element={mkt(<CareManagerPage />)} />
          <Route path="/plans" element={mkt(<PlansPage />)} />
          <Route path="/trust" element={mkt(<Trust />)} />
          <Route path="/about" element={mkt(<About />)} />
          <Route path="/contact" element={mkt(<Contact />)} />
          <Route path="/faq" element={mkt(<FAQ />)} />
          <Route path="/careers" element={mkt(<Careers />)} />
          <Route path="/privacy" element={legal("Privacy Policy", <Privacy />)} />
          <Route path="/terms" element={legal("Terms of Service", <Terms />)} />
          <Route path="/data-deletion" element={legal("Data Deletion", <DataDeletion />)} />
          <Route path="/login" element={NATIVE ? <Login /> : <Marketing><Login /></Marketing>} />
          <Route path="/verify-email" element={<Marketing><VerifyEmail /></Marketing>} />
          <Route path="/forgot-password" element={<Marketing><ForgotPassword /></Marketing>} />
          <Route path="/reset-password" element={<Marketing><ResetPassword /></Marketing>} />
          <Route path="/onboarding" element={NATIVE ? <div className="min-h-screen bg-sand"><AppHeader title="Family setup" back /><Onboarding /></div> : <Marketing><Onboarding /></Marketing>} />

          <Route path="/app" element={<RoleRedirect />} />

          <Route path="/app/family" element={<RequireAuth roles={["family_owner", "family_member"]}>{NATIVE ? <NativeFamilyShell /> : <FamilyLayout />}</RequireAuth>}>
            <Route index element={NATIVE ? <FamilyHome /> : <Overview />} />
            <Route path="care" element={NATIVE ? <FamilyCareHub /> : <Navigate to="/app/family" replace />} />
            <Route path="more" element={NATIVE ? <FamilyMore /> : <Navigate to="/app/family" replace />} />
            <Route path="timeline" element={<Timeline />} />
            <Route path="visits" element={<Visits />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="requests" element={<Requests />} />
            <Route path="members" element={<Members />} />
            <Route path="care-file" element={<CareFile />} />
            <Route path="documents" element={<Documents />} />
            <Route path="billing" element={<Billing />} />
            <Route path="communication" element={<Communication />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          <Route path="/app/senior" element={<RequireAuth roles={["senior", "family_owner", "family_member"]}><SeniorMode /></RequireAuth>} />

          <Route path="/app/cm" element={<RequireAuth roles={["care_manager"]}>{NATIVE ? <NativeCmShell /> : <CMLayout />}</RequireAuth>}>
            <Route index element={<CMToday />} />
            <Route path="families" element={<CmFamilies />} />
            <Route path="visits" element={<CmVisits />} />
            <Route path="alerts" element={<CmAlerts />} />
            <Route path="visit/:id" element={<CMVisit />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          <Route path="/app/admin" element={<RequireAuth roles={["ops_executive", "ops_manager", "admin", "super_admin"]}><AdminLayout /></RequireAuth>}>
            <Route index element={<AdminToday />} />
            <Route path="staff" element={<Staff />} />
            <Route path="customers" element={<Customers />} />
            <Route path="care-managers" element={<CareManagers />} />
            <Route path="services" element={<ServicesAdmin />} />
            <Route path="providers" element={<Providers />} />
            <Route path="emergency" element={<Emergency />} />
            <Route path="cases" element={<Cases />} />
            <Route path="reports" element={<Reports />} />
            <Route path="plans" element={<PlansAdmin />} />
            <Route path="leads" element={<Leads />} />
            <Route path="requests" element={<ServiceRequests />} />
            <Route path="requests/:id" element={<ServiceRequestDetail />} />
            <Route path="visits/:id" element={<VisitDetail />} />
            <Route path="settings/whatsapp" element={<WhatsAppSettings />} />
            <Route path="notifications" element={<AdminNotifications />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          <Route path="*" element={<Navigate to={NATIVE ? "/app" : "/"} replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
